import { Routes, Route, Navigate, useParams } from "react-router-dom";
import { lazy, Suspense } from "react";
import { useAuth } from "./api/auth";
import { LoginPage } from "./pages/LoginPage";
import PwaInstall from './components/PwaInstall';
import OfflineBanner from './components/OfflineBanner';
import { ErrorBoundary } from './components/ErrorBoundary';
import { ToastProvider } from './components/Toast';
import RealtimeProvider from './realtime/RealtimeProvider';
import { useWorkReminder } from './hooks/useWorkReminder';
import { useEffect } from 'react';
import { registerAutoSync } from './utils/queueSync';
import { getAccessToken } from './api/client';

// OperatorShell pidetään eager — se on operaattorin pää-kuori jokaisella /op-reitillä
import OperatorShell from './operator/OperatorShell';

// Manager / ERP-näkymät — lazy-loadataan vain kun reitti vieraillaan
const WeeklyPlanPage = lazy(() => import("./pages/WeeklyPlanPage"));
const ManagerDashboardPage = lazy(() => import("./pages/ManagerDashboardPage"));
const AnalyticsPage = lazy(() => import("./pages/AnalyticsPage"));
const NetvisorPage = lazy(() => import("./pages/NetvisorPage"));
// U-9 (13.7.2026): yksi yhdistetty kalenterisivu kaikille rooleille (pages/calendar/)
const CalendarPage = lazy(() => import("./pages/calendar/CalendarPage"));
const CustomersPage = lazy(() => import("./pages/CustomersPage"));
const CustomerDetailPage = lazy(() => import("./pages/CustomerDetailPage"));
const ProjectsPage = lazy(() => import("./pages/ProjectsPage"));
const ProjectDetailPage = lazy(() => import("./pages/ProjectDetailPage"));
const FormInstancePage = lazy(() => import("./pages/FormInstancePage"));
const InventoryPage = lazy(() => import('./pages/InventoryPage'));
const SdsImportPage = lazy(() => import('./pages/SdsImportPage'));
const ParameterLibraryPage = lazy(() => import('./pages/ParameterLibraryPage'));
const ParameterLibraryDetailPage = lazy(() => import('./pages/ParameterLibraryDetailPage'));
const SuunnittelijaPage = lazy(() => import('./pages/SuunnittelijaPage'));
const SuunnittelijaDetailPage = lazy(() => import('./pages/SuunnittelijaDetailPage'));
const SettingsPage = lazy(() => import('./pages/SettingsPage'));
const DeviationsPage = lazy(() => import('./pages/DeviationsPage'));
const DeviationImportPage = lazy(() => import('./pages/DeviationImportPage'));
const DeviationPdfImportPage = lazy(() => import('./pages/DeviationPdfImportPage'));
const DeviationDetailPage = lazy(() => import('./pages/DeviationDetailPage'));
const PurchaseOrdersPage = lazy(() => import('./pages/PurchaseOrdersPage'));
const OrdersPage = lazy(() => import('./pages/OrdersPage'));
const NssPage = lazy(() => import('./pages/NssPage'));
// OTM-työohjeet (24.8.2026): muurahaishapon vaikutuksen testaus — ohje + pöytäkirja + historia
const OtmPage = lazy(() => import('./pages/otm/OtmPage'));
const PortalHome = lazy(() => import('./portal/PortalHome'));
const PortalBathPage = lazy(() => import('./portal/PortalBathPage'));
const PortalCoatingLog = lazy(() => import('./portal/PortalCoatingLog'));
const PortalProductionReport = lazy(() => import('./portal/PortalProductionReport'));
const PortalLinePage = lazy(() => import('./portal/PortalLinePage'));
const PortalBathsOverview = lazy(() => import('./portal/PortalBathsOverview'));
const CoatingWizard = lazy(() => import('./portal/CoatingWizard'));
// Julkinen kappalesivu (18.8.2026): asiakas avaa erä-QR:stä ilman kirjautumista
const PublicPiecePage = lazy(() => import('./portal/PublicPiecePage'));
const PortalAccount = lazy(() => import('./portal/PortalAccount'));
const CustomerMonitoringPage = lazy(() => import('./pages/CustomerMonitoringPage'));
const PurchaseOrderDetailPage = lazy(() => import('./pages/PurchaseOrderDetailPage'));
const AuditPage = lazy(() => import('./pages/AuditPage'));
const Iso9001ReportsPage = lazy(() => import('./pages/Iso9001ReportsPage'));
const BoardMeetingsPage = lazy(() => import('./pages/board/BoardMeetingsPage'));
const ActionCenterPage = lazy(() => import('./pages/ActionCenterPage'));
const NotificationsPage = lazy(() => import('./pages/NotificationsPage'));
const KnowledgeBasePage = lazy(() => import('./pages/KnowledgeBasePage'));
const KnowledgeSolutionsPage = lazy(() => import('./pages/KnowledgeSolutionsPage'));
const KappaleViewPage = lazy(() => import('./pages/KappaleViewPage'));
const HtmlDocViewPage = lazy(() => import('./pages/html-doc/HtmlDocViewPage'));
// Jaettu raportti (3.7.2026): julkiset vierassivut — asiakas avaa QR:stä ilman kirjautumista
const GuestBatchPage = lazy(() => import('./guest/GuestBatchPage'));
const TietosuojaPage = lazy(() => import('./pages/TietosuojaPage'));
const GuestPiecePage = lazy(() => import('./guest/GuestPiecePage'));
// Hallitusportaali (14.7.2026): julkinen jäsensivu — turvalinkki ilman kirjautumista
const BoardPortalPage = lazy(() => import('./pages/board/BoardPortalPage'));
// Hallin infonäyttö (19.8.2026): julkinen tokenoitu live-TV-sivu — EI kirjautumista
const InfoDisplayPage = lazy(() => import('./pages/tv/InfoDisplayPage'));
// Korroosiovahti (19.8.2026): korroosiotestien analyysisivu kirjautuneille
const KorroosiovahtiPage = lazy(() => import('./pages/korroosiovahti/KorroosiovahtiPage'));
// Tulosten pikasyöttö (19.8.2026): arvo kerrallaan, heti tallessa
const TuloksetPage = lazy(() => import('./pages/tulokset/TuloksetPage'));
// Johto-MEA (3.–6.7.2026): johdon MEA-sivut — U-1:stä lähtien SAMASSA
// OperatorShell-kuoressa kuin operaattorisivut (yksi yhtenäinen ERP-kokemus)
const ExecMorePage = lazy(() => import('./exec/ExecMorePage'));
const HullPage = lazy(() => import('./pages/HullPage'));
const AssistantPage = lazy(() => import('./pages/AssistantPage'));
const UsersPage = lazy(() => import('./pages/UsersPage'));
const TimeTrackingPage = lazy(() => import('./pages/TimeTrackingPage'));
const ApprovalsPage = lazy(() => import('./pages/ApprovalsPage'));
const BathsPage = lazy(() => import('./pages/BathsPage'));
// Kylvyt 2 (19.8.2026): rinnakkainen selkeämpi kylpylista — vanha /baths säilyy ennallaan
const Kylvyt2Page = lazy(() => import('./pages/kylvyt2/Kylvyt2Page'));
const BathDetailPage = lazy(() => import('./pages/BathDetailPage'));
// APCRK-anturidata (31.7.2026): liukulinjan reaaliaikainen seuranta
const LineMonitorPage = lazy(() => import('./pages/line-monitor/LineMonitorPage'));
const SlideLineHubPage = lazy(() => import('./operator/slide-line/SlideLineHubPage'));
const LinjanakymaPage = lazy(() => import('./pages/linjanakyma/LinjanakymaPage'));
// Viikkopalaveri (20.8.2026): maanantaipalaverin runko yhdellä sivulla
const ViikkopalaveriPage = lazy(() => import('./pages/viikkopalaveri/ViikkopalaveriPage'));
// 👥 Työnjako (25.8.2026): päivän työt kaikille kolmella klikkauksella — helpompi
// koko näytön käyttöliittymä viikkosuunnitelman pikasyötön (#514) kirjoituspolun päälle
const TyonjakoPage = lazy(() => import('./pages/tyonjako/TyonjakoPage'));
const BathChemicalsPage = lazy(() => import('./pages/BathChemicalsPage'));
const WeeklySummaryPage = lazy(() => import('./pages/WeeklySummaryPage'));
const OperatorSkillsPage = lazy(() => import('./pages/OperatorSkillsPage'));
const InspectionBuilderPage = lazy(() => import('./pages/InspectionBuilderPage'));
const InspectionReportPage = lazy(() => import('./pages/InspectionReportPage'));
const WarehouseScanPage = lazy(() => import('./pages/WarehouseScanPage'));
const FaradayPage = lazy(() => import('./pages/FaradayPage'));
const StrategyPage = lazy(() => import('./pages/StrategyPage'));
// Sprint 33: ExecutiveCockpitPage poistettu — yhdistetty ManagerDashboardPage:hen (Tilannekuva).
// /executive-cockpit redirect /manager-dashboardiin
const AlignmentFlagsPage = lazy(() => import('./pages/AlignmentFlagsPage'));
const HrPage = lazy(() => import('./pages/HrPage'));
const TechnologyPage = lazy(() => import('./pages/TechnologyPage'));
const ProductionLinesPage = lazy(() => import('./pages/ProductionLinesPage'));
const PartnersPage = lazy(() => import('./pages/PartnersPage'));
const SalesPage = lazy(() => import('./pages/SalesPage'));
const RDPage = lazy(() => import('./pages/RDPage'));
const QualityPage = lazy(() => import('./pages/QualityPage'));
const FinancePage = lazy(() => import('./pages/FinancePage'));
// TripleHard-talousmalli (21.8.2026): ARR/royalty-malli 2026–2032 + skenaariot,
// herkkyysanalyysi, ERP-linjadata ja vuosituotannon simulaatio — CEO + admin
const TalousmalliPage = lazy(() => import('./pages/talousmalli/TalousmalliPage'));
// DD-dataroom (23.8.2026): due diligence -pyynnöt, dokumentit, sijoittajakutsut + pääsyloki
const DataroomPage = lazy(() => import('./pages/dataroom/DataroomPage'));
// Sijoittajan dataroom-näkymä: JULKINEN tokenoitu READ-ONLY-reitti (ei kirjautumista)
const DataroomInvestorPage = lazy(() => import('./pages/dataroom/InvestorPage'));
const BriefingsPage = lazy(() => import('./pages/BriefingsPage'));
const InvestorPortalPage = lazy(() => import('./pages/InvestorPortalPage'));
const TenantsPage = lazy(() => import('./pages/TenantsPage'));
// AI-mallit (22.7.2026): mallien hallinta ilman deployta — admin + CEO
const AiModelsPage = lazy(() => import('./pages/AiModelsPage'));
const InventoryChecksListPage = lazy(() => import('./pages/InventoryChecksListPage'));
const InventoryCheckPage = lazy(() => import('./pages/InventoryCheckPage'));

// ── MEA-tyylinen ohjattu operaattorikokemus ── (lazy: ladataan vain operaattorille)
const TodayPage = lazy(() => import('./operator/TodayPage'));
const WorkPage = lazy(() => import('./operator/WorkPage'));
const AiPage = lazy(() => import('./operator/AiPage'));
const MyPage = lazy(() => import('./operator/MyPage'));
const MorePage = lazy(() => import('./operator/MorePage'));
const OperatorResearchListPage = lazy(() => import('./operator/OperatorResearchListPage'));
const OperatorResearchCampaignPage = lazy(() => import('./operator/OperatorResearchCampaignPage'));
const OperatorResearchBathsPage = lazy(() => import('./operator/OperatorResearchBathsPage'));
const OperatorResearchAnalysisPage = lazy(() => import('./operator/OperatorResearchAnalysisPage'));
const TestBathStandardStart = lazy(() => import('./operator/test-bath-standard/TestBathStandardStart'));
const TestBathStandardWizard = lazy(() => import('./operator/test-bath-standard/TestBathStandardWizard'));
// ⚡ Pikapinnoitus (10.8.2026): listasivu + wizard-reitti
const QuickCoatListPage = lazy(() => import('./operator/quick-coat/QuickCoatListPage'));
const QuickCoatWizardPage = lazy(() => import('./operator/quick-coat/QuickCoatWizardPage'));
const CalibrationsPage = lazy(() => import('./pages/CalibrationsPage'));
const OperatorSettingsPage = lazy(() => import('./operator/OperatorSettingsPage'));
const OperatorMessagesPage = lazy(() => import('./operator/OperatorMessagesPage'));

function WorkReminder() {
  const { isAuthenticated } = useAuth();
  const { showReminder, elapsedMinutes, dismiss } = useWorkReminder(isAuthenticated);
  if (!showReminder) return null;

  const h = Math.floor((elapsedMinutes ?? 0) / 60);
  const m = (elapsedMinutes ?? 0) % 60;

  return (
    <div style={{
      // 19.8.2026 (desktop-skaalaus): tämä palkki on kuoren ULKOPUOLELLA (ei
      // zoomia), mutta 56 px on viritetty kuoren 52 px -headeriin joka kasvaa
      // zoomin mukana. --app-zoom on määritelty myös :root-tasolla (OperatorShell)
      // → skaalataan offset samassa tahdissa. Kuoren ulkopuolisilla sivuilla
      // muuttujaa ei ole → fallback 1 = entinen 56 px.
      position: 'fixed', top: 'calc(56px * var(--app-zoom, 1))', left: 0, right: 0, zIndex: 999,
      background: '#d97706', color: '#fff',
      padding: '10px 20px',
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      fontFamily: 'var(--mono, monospace)', fontSize: 12, letterSpacing: '.04em',
      boxShadow: '0 2px 8px rgba(0,0,0,.2)',
    }}>
      <span>⏰ Muista lopettaa työaika — olet työskennellyt {h}t {m}min</span>
      <button onClick={dismiss} style={{
        background: 'rgba(255,255,255,.2)', border: 'none', color: '#fff',
        cursor: 'pointer', padding: '2px 10px', borderRadius: 4, fontSize: 11,
        fontFamily: 'inherit',
      }}>
        Kuittaa
      </button>
    </div>
  );
}

// U-4: /op/projects/:id yhdistyi /projects/:id-sivuun — vanhat linkit (PWA,
// viestit, QR) ohjataan yhdistetylle sivulle.
function RedirectOpProject() {
  const { id } = useParams<{ id: string }>();
  return <Navigate to={`/projects/${id}`} replace />;
}

// U-7: /op/baths/:id yhdistyi /baths/:id-sivuun.
function RedirectOpBath() {
  const { id } = useParams<{ id: string }>();
  return <Navigate to={`/baths/${id}`} replace />;
}

function RequireAuth({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth();
  if (isLoading) return null;
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  return <>{children}</>;
}

// Jaettu raportti (3.7.2026): /kappale/:ref toimii KAHDESSA moodissa — kirjautunut
// näkee täyden sisäisen näkymän, kirjautumaton (asiakas skannasi tunnistetarran)
// vierasnäkymän JOS kappaleen projektilla on aktiivinen jako (muuten 🔒-viesti).
function KappaleRoute() {
  const { user, isAuthenticated, isLoading } = useAuth();
  if (isLoading) return null;
  // 6.7.2026 (bugijahti): kirjautunut PORTAALIASIAKAS ohjataan vierasnäkymään —
  // sisäinen KappaleViewPage kutsuu /pieces-endpointteja jotka guard estää
  // customer-roolilta (403 → rikkinäinen sivu QR-skannauksesta).
  if (user?.role === 'customer') return <GuestPiecePage />;
  return isAuthenticated ? <KappaleViewPage /> : <GuestPiecePage />;
}

// Sprint 33: RequireRole — suojaa reitti rooli-listalle. Muut roolit
// ohjataan oletuksena /-juureen (DefaultRoute reitittää sieltä eteenpäin).
function RequireRole({ allow, children }: { allow: string[]; children: React.ReactNode }) {
  const { user, isAuthenticated, isLoading } = useAuth();
  if (isLoading) return null;
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (!user?.role || !allow.includes(user.role)) return <Navigate to="/" replace />;
  return <>{children}</>;
}

// Sprint 35 / H1.B: RequirePage — suojaa reitti pageKey:n perusteella käyttäen
// dynaamista user.allowedPages-listaa. Admin pääsee aina (backend täyttää
// allowedPages-listalla kaikki sivut adminille). Muille roolelle taso tulee DB:stä.
function RequirePage({ pageKey, children }: { pageKey: string; children: React.ReactNode }) {
  const { user, isAuthenticated, isLoading } = useAuth();
  if (isLoading) return null;
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  // Backward-compatible: jos user.allowedPages puuttuu (esim. vanha JWT-token),
  // sallitaan pääsy (backend RolesGuard estää kuitenkin jos tarpeen).
  if (user?.allowedPages && !user.allowedPages.includes(pageKey)) {
    return <Navigate to="/" replace />;
  }
  return <>{children}</>;
}

// Roolipohjainen oletussivu: operator → MEA-tyylinen ohjattu kokemus,
// admin/ceo → Tilannekuva (manager-dashboard), manager → projektit, muut → kalenteri.
// Sprint H1.A: Tilannekuva + Viikko vain admin/ceo. Manager menee projekteihin.
function DefaultRoute() {
  const { user } = useAuth();
  if (user?.role === 'customer') return <Navigate to="/portal" replace />; // asiakasportaali
  // U-1 (6.7.2026): KAIKKI sisäiset roolit samaan yhtenäiseen kuoreen — johto saa
  // OperatorShellissä laajennetut välilehdet (Tilannekuva + Projektit + johdon Lisää)
  if (user?.role === 'operator' || user?.role === 'admin' || user?.role === 'ceo' || user?.role === 'manager') {
    return <Navigate to="/op" replace />;
  }
  return <CalendarPage />;
}

function PageLoading() {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      minHeight: '60vh', color: '#64748b', fontSize: 13,
      fontFamily: "var(--font-ui)",
    }}>
      Ladataan…
    </div>
  );
}

export default function App() {
  // Käynnistä mutation queue -synkronointi: ajaa olemassa olevat offline-mutaatiot
  // läpi heti mountissa (jos verkkoa) ja triggeröityy 'online'-eventillä.
  useEffect(() => {
    registerAutoSync(getAccessToken);
  }, []);

  return (
    <ErrorBoundary>
    <ToastProvider>
    <RealtimeProvider>
      <OfflineBanner />
      <WorkReminder />
      <PwaInstall />
      <Suspense fallback={<PageLoading />}>
      <Routes>
      <Route path="/login" element={<LoginPage />} />
      {/* Jaettu raportti (3.7.2026): JULKINEN — asiakas avaa erä-QR:stä ilman kirjautumista */}
      <Route path="/jaettu/:token" element={<GuestBatchPage />} />
      {/* Hallitusportaali (14.7.2026): JULKINEN — jäsen avaa henkilökohtaisesta turvalinkistä */}
      <Route path="/hallitus-portaali/:token" element={<BoardPortalPage />} />
      {/* Pinnoitetun kappaleen kuvat (18.8.2026): JULKINEN — asiakas avaa erä-QR:stä ilman kirjautumista */}
      <Route path="/p/:token" element={<PublicPiecePage />} />
      {/* Hallin infonäyttö — julkinen tokenoitu TV-sivu (ei kirjautumista) */}
      <Route path="/tv/:token" element={<InfoDisplayPage />} />
      {/* DD-dataroom sijoittajalle (23.8.2026): JULKINEN — kutsutoken, READ-ONLY, fail-closed */}
      <Route path="/dd/:token" element={<DataroomInvestorPage />} />
      {/* Tietosuojaseloste (4.9.2026): JULKINEN — App Store Connectin Privacy Policy -URL, Apple avaa ilman tunnuksia */}
      <Route path="/tietosuoja" element={<TietosuojaPage />} />

      {/* ── Johto-MEA U-1 (6.7.2026): YKSI kuori — johdon lisäsivut OperatorShellissä.
          Tänään/Työ/AI ovat samat aidot operaattorisivut (/op, /op/work, /op/ai). ── */}
      <Route path="/exec" element={<Navigate to="/op" replace />} />
      <Route path="/exec/work" element={<Navigate to="/op/work" replace />} />
      <Route path="/exec/ai" element={<Navigate to="/op/ai" replace />} />
      {/* 7.7.2026 (palaute): tiivistelmäsivut poistettu — YKSI täysi sivu per toiminto */}
      <Route path="/exec/tilannekuva" element={<Navigate to="/manager-dashboard" replace />} />
      <Route path="/exec/projects" element={<Navigate to="/projects" replace />} />
      <Route path="/exec/more" element={<RequireRole allow={['manager', 'admin', 'ceo']}><OperatorShell><ExecMorePage /></OperatorShell></RequireRole>} />

      {/* ── MEA-tyylinen ohjattu operaattorikokemus ── */}
      <Route path="/op" element={<RequireAuth><OperatorShell><TodayPage /></OperatorShell></RequireAuth>} />
      <Route path="/op/work" element={<RequireAuth><OperatorShell><WorkPage /></OperatorShell></RequireAuth>} />
      <Route path="/op/work/*" element={<RequireAuth><OperatorShell><WorkPage /></OperatorShell></RequireAuth>} />
      {/* Sprint G3.1: 5L standarditesti */}
      <Route path="/op/test-bath-standard" element={<RequireAuth><OperatorShell><TestBathStandardStart /></OperatorShell></RequireAuth>} />
      <Route path="/op/test-bath-standard/:id" element={<RequireAuth><OperatorShell><TestBathStandardWizard /></OperatorShell></RequireAuth>} />
      {/* ⚡ Pikapinnoitus (10.8.2026): lista + wizard (sama komponentti kuin Työ-sivun haarassa) */}
      <Route path="/op/pikapinnoitukset" element={<RequireAuth><OperatorShell><QuickCoatListPage /></OperatorShell></RequireAuth>} />
      <Route path="/op/pikapinnoitukset/:id" element={<RequireAuth><OperatorShell><QuickCoatWizardPage /></OperatorShell></RequireAuth>} />
      {/* U-9: /op/calendar yhdistyi /calendar-sivuun */}
      <Route path="/op/calendar" element={<RequireAuth><Navigate to="/calendar" replace /></RequireAuth>} />
      <Route path="/op/messages" element={<RequireAuth><OperatorShell><OperatorMessagesPage /></OperatorShell></RequireAuth>} />
      {/* Sprint G6: Kalibrointirekisteri */}
      <Route path="/calibrations" element={<RequireAuth><OperatorShell><CalibrationsPage /></OperatorShell></RequireAuth>} />
      {/* Sprint G7: Operaattorin asetukset */}
      <Route path="/op/settings" element={<RequireAuth><OperatorShell><OperatorSettingsPage /></OperatorShell></RequireAuth>} />
      {/* Sprint 38 / H2.B: päivän väliraportti */}
      <Route path="/op/ai" element={<RequireAuth><OperatorShell><AiPage /></OperatorShell></RequireAuth>} />
      <Route path="/op/more" element={<RequireAuth><OperatorShell><MorePage /></OperatorShell></RequireAuth>} />
      <Route path="/op/me" element={<RequireAuth><OperatorShell><MyPage /></OperatorShell></RequireAuth>} />
      {/* U-8: /op/inventory yhdistyi /inventory-sivuun */}
      <Route path="/op/inventory" element={<RequireAuth><Navigate to="/inventory" replace /></RequireAuth>} />
      {/* U-12 (14.7.2026): yhdistetyt Tilaukset + NSS — /op-variantit ohjaavat */}
      <Route path="/op/tilaukset" element={<RequireAuth><Navigate to="/tilaukset" replace /></RequireAuth>} />
      <Route path="/op/suolasumutestit" element={<RequireAuth><Navigate to="/suolasumutestit" replace /></RequireAuth>} />
      {/* U-7: /op/baths(/:id) yhdistyi /baths(/:id)-sivuihin */}
      <Route path="/op/baths" element={<RequireAuth><Navigate to="/baths" replace /></RequireAuth>} />
      <Route path="/op/baths/:id" element={<RequireAuth><RedirectOpBath /></RequireAuth>} />
      {/* U-11 (14.7.2026): yhdistetty projektilista — /op/projektit ohjaa /projects-sivulle */}
      <Route path="/op/projektit" element={<RequireAuth><Navigate to="/projects" replace /></RequireAuth>} />
      {/* Vaihe E (28.5.2026): operaattorin minimaalinen projektinäkymä — viestien chip-klikkaus ohjaa tänne */}
      <Route path="/op/projects/:id" element={<RequireAuth><RedirectOpProject /></RequireAuth>} />
      {/* U-5: /op/deviations yhdistyi /deviations-sivuun */}
      <Route path="/op/deviations" element={<RequireAuth><Navigate to="/deviations" replace /></RequireAuth>} />
      <Route path="/op/koeajot" element={<RequireAuth><OperatorShell><OperatorResearchListPage /></OperatorShell></RequireAuth>} />
      <Route path="/op/koeajot/kylvyt" element={<RequireAuth><OperatorShell><OperatorResearchBathsPage /></OperatorShell></RequireAuth>} />
      <Route path="/op/koeajot/analyysi" element={<RequireAuth><OperatorShell><OperatorResearchAnalysisPage /></OperatorShell></RequireAuth>} />
      <Route path="/op/koeajot/:id" element={<RequireAuth><OperatorShell><OperatorResearchCampaignPage /></OperatorShell></RequireAuth>} />

      {/* Sprint G3: Asetukset-sivu kaikille rooleille */}
      <Route path="/asetukset" element={<RequireAuth><OperatorShell><SettingsPage /></OperatorShell></RequireAuth>} />
      {/* Sprint H-korjaus: /op/asetukset → /op/settings (vanha SettingsPage sisälsi
          TopNav:in joka näkyi väärin OperatorShell:n sisällä). MEA-tyylinen
          OperatorSettingsPage on oikea sivu operaattorille. */}
      <Route path="/op/asetukset" element={<Navigate to="/op/settings" replace />} />

      {/* Vanhat operator-reitit → redirect uusiin /op-reitteihin */}
      <Route path="/operator/start" element={<Navigate to="/op" replace />} />
      <Route path="/operator/work-start" element={<Navigate to="/op/work" replace />} />
      <Route path="/operator/test-run" element={<Navigate to="/op/work" replace />} />
      <Route path="/operator/inspection" element={<Navigate to="/op/work" replace />} />
      <Route path="/operator/day-end" element={<Navigate to="/op" replace />} />
      <Route path="/operator-metrics" element={<Navigate to="/op/me" replace />} />
      {/* Sprint 35 / H1.B: dynaaminen RequirePage käyttää DB-pohjaista oikeushallintaa */}
      <Route path="/weekly-plan" element={<RequirePage pageKey="viikko"><OperatorShell><WeeklyPlanPage /></OperatorShell></RequirePage>} />
      <Route path="/manager-dashboard" element={<RequirePage pageKey="tilannekuva"><OperatorShell><ManagerDashboardPage /></OperatorShell></RequirePage>} />
      <Route path="/analytics" element={<RequirePage pageKey="analytiikka"><OperatorShell><AnalyticsPage /></OperatorShell></RequirePage>} />
      <Route path="/integrations/netvisor" element={<RequireAuth><OperatorShell><NetvisorPage /></OperatorShell></RequireAuth>} />

      {/* Oletussivu: roolipohjainen redirect */}
      <Route path="/" element={<RequireAuth><DefaultRoute /></RequireAuth>} /> {/* pelkkä redirect — ei kuorta (customer-välähdys, bugijahti #7) */}

      {/* Hallintanäkymä (kaikille, backend-guardit rajoittavat) */}
      <Route path="/customers" element={<RequireAuth><OperatorShell><CustomersPage /></OperatorShell></RequireAuth>} />
      <Route path="/customers/:id" element={<RequireAuth><OperatorShell><CustomerDetailPage /></OperatorShell></RequireAuth>} />
      <Route path="/projects" element={<RequireAuth><OperatorShell><ProjectsPage /></OperatorShell></RequireAuth>} />
      <Route path="/projects/:id" element={<RequireAuth><OperatorShell><ProjectDetailPage /></OperatorShell></RequireAuth>} />
      <Route path="/forms/:id" element={<RequireAuth><OperatorShell><FormInstancePage /></OperatorShell></RequireAuth>} />
      <Route path="/inventory" element={<RequireAuth><OperatorShell><InventoryPage /></OperatorShell></RequireAuth>} />
      <Route path="/inventory/ktt-tuonti" element={<RequireRole allow={['admin', 'manager']}><OperatorShell><SdsImportPage /></OperatorShell></RequireRole>} />
      <Route path="/parameter-library" element={<RequireAuth><OperatorShell><ParameterLibraryPage /></OperatorShell></RequireAuth>} />
      <Route path="/parameter-library/new" element={<RequireAuth><OperatorShell><ParameterLibraryDetailPage /></OperatorShell></RequireAuth>} />
      <Route path="/parameter-library/:id" element={<RequireAuth><OperatorShell><ParameterLibraryDetailPage /></OperatorShell></RequireAuth>} />
      <Route path="/suunnittelija" element={<RequireAuth><OperatorShell><SuunnittelijaPage /></OperatorShell></RequireAuth>} />
      <Route path="/suunnittelija/:id" element={<RequireAuth><OperatorShell><SuunnittelijaDetailPage /></OperatorShell></RequireAuth>} />
      <Route path="/deviations" element={<RequireAuth><OperatorShell><DeviationsPage /></OperatorShell></RequireAuth>} />
      <Route path="/deviations/import" element={<RequireRole allow={['admin', 'manager']}><OperatorShell><DeviationImportPage /></OperatorShell></RequireRole>} />
      <Route path="/deviations/pdf-import" element={<RequireRole allow={['admin', 'manager']}><OperatorShell><DeviationPdfImportPage /></OperatorShell></RequireRole>} />
      <Route path="/deviations/:id" element={<RequireAuth><OperatorShell><DeviationDetailPage /></OperatorShell></RequireAuth>} />
      <Route path="/purchase-orders" element={<RequireAuth><OperatorShell><PurchaseOrdersPage /></OperatorShell></RequireAuth>} />
      <Route path="/tilaukset" element={<RequireAuth><OperatorShell><OrdersPage /></OperatorShell></RequireAuth>} />
      <Route path="/suolasumutestit" element={<RequireAuth><OperatorShell><NssPage /></OperatorShell></RequireAuth>} />
      {/* OTM: Muurahaishappotesti (24.8.2026) — työohje, testipöytäkirja ja historia yhdellä sivulla */}
      <Route path="/otm" element={<RequireAuth><OperatorShell><OtmPage /></OperatorShell></RequireAuth>} />
      {/* Tulosten pikasyöttö (19.8.2026): kovuus, adheesio, paksuus ja arvosana parilla napautuksella */}
      <Route path="/tulokset" element={<RequireAuth><OperatorShell><TuloksetPage /></OperatorShell></RequireAuth>} />
      {/* Infonäyttö kirjautuneille (19.8.2026): sama näkymä kuin hallin TV:ssä, ilman tokenia */}
      <Route path="/infonaytto" element={<RequireAuth><InfoDisplayPage /></RequireAuth>} />
      {/* Korroosiovahti (19.8.2026): analyysisivu ILMAN OperatorShelliä — oma vaalea/tumma teema */}
      <Route path="/korroosiovahti" element={<RequireAuth><KorroosiovahtiPage /></RequireAuth>} />
      {/* Asiakasportaali — customer-rooli (DefaultRoute ohjaa tänne) */}
      <Route path="/portal" element={<RequireAuth><PortalHome /></RequireAuth>} />
      <Route path="/portal/baths/:id" element={<RequireAuth><PortalBathPage /></RequireAuth>} />
      <Route path="/portal/baths/:id/coating" element={<RequireAuth><PortalCoatingLog /></RequireAuth>} />
      {/* 📊 Tuotantoraportti (18.9.2026): käyttöaste, laatu, määrät — päivä/viikko/kuukausi ja asiakkaittain */}
      <Route path="/portal/baths/:id/raportti" element={<RequireAuth><PortalProductionReport /></RequireAuth>} />
      {/* 🧪 Kemikaalit ja huolto (20.9.2026): koko linja — peittaus, huuhtelut, nikkeli, kromi — ja sen huoltoloki */}
      <Route path="/portal/baths/:id/linja" element={<RequireAuth><PortalLinePage /></RequireAuth>} />
      {/* 🛁 Kylpyjen tila -yleisnäkymä (20.8.2026): kaikki kylvyt kuvaajineen */}
      <Route path="/portal/kylvyt" element={<RequireAuth><PortalBathsOverview /></RequireAuth>} />
      {/* 🚀 Pinnoita kappale -wizard (20.8.2026): ohjattu kirjaus etusivulta */}
      <Route path="/portal/pinnoita" element={<RequireAuth><CoatingWizard /></RequireAuth>} />
      {/* 24.9.2026: tili ja tuki — natiivissa 18.9. alkaen, selaimesta puuttui
          eikä asiakas päässyt vaihtamaan salasanaansa mitään kautta. */}
      <Route path="/portal/tili" element={<RequireAuth><PortalAccount /></RequireAuth>} />
      {/* Savrocin sisäinen asiakasportaali-monitorointi */}
      <Route path="/asiakasportaali" element={<RequireAuth><OperatorShell><CustomerMonitoringPage /></OperatorShell></RequireAuth>} />
      <Route path="/purchase-orders/:id" element={<RequireAuth><OperatorShell><PurchaseOrderDetailPage /></OperatorShell></RequireAuth>} />
      {/* Sprint 35 / H1.B: dynaaminen — admin pääsee aina backendin allowedPages-listassa */}
      <Route path="/audit" element={<RequirePage pageKey="audit"><OperatorShell><AuditPage /></OperatorShell></RequirePage>} />
      <Route path="/raportit/iso9001" element={<RequireAuth><OperatorShell><Iso9001ReportsPage /></OperatorShell></RequireAuth>} />
      <Route path="/hallitus" element={<RequirePage pageKey="hallitus"><OperatorShell><BoardMeetingsPage /></OperatorShell></RequirePage>} />
      <Route path="/action-center" element={<RequireAuth><OperatorShell><ActionCenterPage /></OperatorShell></RequireAuth>} />
      <Route path="/notifications" element={<RequireAuth><OperatorShell><NotificationsPage /></OperatorShell></RequireAuth>} />
      <Route path="/knowledge-base" element={<RequireAuth><OperatorShell><KnowledgeBasePage /></OperatorShell></RequireAuth>} />
      <Route path="/ratkaisut" element={<RequireAuth><OperatorShell><KnowledgeSolutionsPage /></OperatorShell></RequireAuth>} />
      {/* Kaksimoodinen: kirjautunut → sisäinen näkymä; vieras → jaettu kappalenäkymä (3.7.2026) */}
      <Route path="/kappale/:ref" element={<KappaleRoute />} />
      {/* Aktiivinen HTML-dokumentti koko ruudulla — ei OperatorShell-kuorta (10.8.2026) */}
      <Route path="/dokumentti/:id/html" element={<RequireAuth><HtmlDocViewPage /></RequireAuth>} />
      <Route path="/hull" element={<RequireAuth><OperatorShell><HullPage /></OperatorShell></RequireAuth>} />
      <Route path="/assistant" element={<RequireAuth><OperatorShell><AssistantPage /></OperatorShell></RequireAuth>} />
      {/* Sprint 35 / H1.B: dynaaminen — admin pääsee aina (page-registry: defaultRoles=[]) */}
      <Route path="/users" element={<RequirePage pageKey="kayttajat"><OperatorShell><UsersPage /></OperatorShell></RequirePage>} />
      <Route path="/time-tracking" element={<RequireAuth><OperatorShell><TimeTrackingPage /></OperatorShell></RequireAuth>} />
      <Route path="/approvals" element={<RequireAuth><OperatorShell><ApprovalsPage /></OperatorShell></RequireAuth>} />
      <Route path="/baths" element={<RequireAuth><OperatorShell><BathsPage /></OperatorShell></RequireAuth>} />
      {/* Kylvyt 2 — sama suojaus kuin /baths, vertailtavissa rinnakkain */}
      <Route path="/kylvyt2" element={<RequireAuth><OperatorShell><Kylvyt2Page /></OperatorShell></RequireAuth>} />
      <Route path="/liukulinja" element={<RequirePage pageKey="liukulinja"><OperatorShell><LineMonitorPage /></OperatorShell></RequirePage>} />
      {/* Jatkuva liukulinjaoptimoinnin työsivu (13.8.2026) — sessiot + ajot + kappaleet */}
      <Route path="/liukulinja-ajot" element={<RequirePage pageKey="liukulinja-ajot"><OperatorShell><SlideLineHubPage /></OperatorShell></RequirePage>} />
      {/* Livenäkymä (19.8.2026): missä kohtaa linjaa kappaleet ovat juuri nyt.
          Sama pageKey kuin ajoilla — sama yleisö, ei uutta oikeutta tuotantoon. */}
      <Route path="/linjanakyma" element={<RequirePage pageKey="liukulinja-ajot"><OperatorShell><LinjanakymaPage /></OperatorShell></RequirePage>} />
      {/* Viikkopalaveri (20.8.2026): maanantain palaverirunko. Sama pageKey kuin
          viikkosuunnitelmalla — sama yleisö ja sama data, ei uutta sivuoikeutta
          tuotantoon. Jos operaattorien pitää päästä mukaan, admin avaa `viikko`
          heille Käyttäjät → Sivuoikeudet -välilehdeltä. */}
      <Route path="/viikkopalaveri" element={<RequirePage pageKey="viikko"><OperatorShell><ViikkopalaveriPage /></OperatorShell></RequirePage>} />
      {/* 👥 Työnjako (25.8.2026): työ kaikille kolmella klikkauksella. Sama pageKey
          kuin viikkosuunnitelmalla — sama yleisö (manager/admin/ceo), ei uutta
          sivuoikeutta. Operaattorilla on oma "Lisää itsellesi" Tänään-sivulla —
          tätä sivua ei näytetä heille. */}
      <Route path="/tyonjako" element={<RequirePage pageKey="viikko"><OperatorShell><TyonjakoPage /></OperatorShell></RequirePage>} />
      <Route path="/baths/chemicals" element={<RequireAuth><OperatorShell><BathChemicalsPage /></OperatorShell></RequireAuth>} />
      {/* U-6: viikkoreflektio kuoreen (oli paljas sivu ilman navigointia) */}
      <Route path="/operator/weekly-summary" element={<RequireAuth><OperatorShell><WeeklySummaryPage /></OperatorShell></RequireAuth>} />
      <Route path="/operator/skills" element={<RequireAuth><OperatorSkillsPage /></RequireAuth>} />
      <Route path="/inspections/new" element={<RequireAuth><OperatorShell><InspectionBuilderPage /></OperatorShell></RequireAuth>} />
      <Route path="/inspections/:id" element={<RequireAuth><OperatorShell><InspectionReportPage /></OperatorShell></RequireAuth>} />
      <Route path="/warehouse/scan" element={<RequireAuth><OperatorShell><WarehouseScanPage /></OperatorShell></RequireAuth>} />
      <Route path="/faraday" element={<RequireAuth><OperatorShell><FaradayPage /></OperatorShell></RequireAuth>} />
      <Route path="/strategy" element={<RequireAuth><OperatorShell><StrategyPage /></OperatorShell></RequireAuth>} />
      {/* Sprint 33: /executive-cockpit redirect /manager-dashboardiin (yhdistetty Tilannekuvaan) */}
      <Route path="/executive-cockpit" element={<Navigate to="/manager-dashboard" replace />} />
      <Route path="/alignment-flags" element={<RequireAuth><OperatorShell><AlignmentFlagsPage /></OperatorShell></RequireAuth>} />
      <Route path="/hr" element={<RequireAuth><OperatorShell><HrPage /></OperatorShell></RequireAuth>} />
      <Route path="/technology" element={<RequireAuth><OperatorShell><TechnologyPage /></OperatorShell></RequireAuth>} />
      <Route path="/production-lines" element={<RequireAuth><OperatorShell><ProductionLinesPage /></OperatorShell></RequireAuth>} />
      <Route path="/partners" element={<RequireAuth><OperatorShell><PartnersPage /></OperatorShell></RequireAuth>} />
      <Route path="/sales" element={<RequireAuth><OperatorShell><SalesPage /></OperatorShell></RequireAuth>} />
      <Route path="/rd" element={<RequireAuth><OperatorShell><RDPage /></OperatorShell></RequireAuth>} />
      <Route path="/quality" element={<RequireAuth><OperatorShell><QualityPage /></OperatorShell></RequireAuth>} />
      <Route path="/finance" element={<RequireAuth><OperatorShell><FinancePage /></OperatorShell></RequireAuth>} />
      {/* TripleHard-talousmalli — DD-materiaalia, oikeus page-registryn kautta (ceo + admin) */}
      <Route path="/talousmalli" element={<RequirePage pageKey="talousmalli"><OperatorShell><TalousmalliPage /></OperatorShell></RequirePage>} />
      {/* DD-dataroom — DD-materiaalia, oikeus page-registryn kautta (ceo + admin) */}
      <Route path="/dataroom" element={<RequirePage pageKey="dataroom"><OperatorShell><DataroomPage /></OperatorShell></RequirePage>} />
      <Route path="/briefings" element={<RequireAuth><OperatorShell><BriefingsPage /></OperatorShell></RequireAuth>} />
      <Route path="/investor" element={<RequireAuth><OperatorShell><InvestorPortalPage /></OperatorShell></RequireAuth>} />
      <Route path="/tenants" element={<RequireRole allow={['admin']}><OperatorShell><TenantsPage /></OperatorShell></RequireRole>} />
      <Route path="/ai-mallit" element={<RequireRole allow={['admin', 'ceo']}><OperatorShell><AiModelsPage /></OperatorShell></RequireRole>} />
      <Route path="/inventory-checks" element={<RequireAuth><OperatorShell><InventoryChecksListPage /></OperatorShell></RequireAuth>} />
      <Route path="/inventory-checks/:id" element={<RequireAuth><OperatorShell><InventoryCheckPage /></OperatorShell></RequireAuth>} />
      <Route path="/baths/:id" element={<RequireAuth><OperatorShell><BathDetailPage /></OperatorShell></RequireAuth>} />
      <Route path="/calendar" element={<RequireAuth><OperatorShell><CalendarPage /></OperatorShell></RequireAuth>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </Suspense>
    </RealtimeProvider>
    </ToastProvider>
    </ErrorBoundary>
  );
}
